export function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

export function toCamelCase(input) {
  if (!input || typeof input !== "string") {
    return "";
  }
  return input
    .replace(/^mdp-readabler-/, "")
    .replace(/^readlygo-action-/, "")
    .replace(/^readlygo-/, "")
    .replace(/^action-/, "")
    .replace(/^mdp-action-/, "")
    .replace(/[-_ ]+([a-zA-Z0-9])/g, (_, char) => char.toUpperCase())
    .replace(/^[A-Z]/, (first) => first.toLowerCase());
}

export function deepMerge(base, override) {
  if (!isObject(base) || !isObject(override)) {
    return override === undefined ? base : override;
  }

  const result = { ...base };
  for (const [key, value] of Object.entries(override)) {
    if (Array.isArray(value)) {
      result[key] = [...value];
      continue;
    }
    if (isObject(value) && isObject(base[key])) {
      result[key] = deepMerge(base[key], value);
      continue;
    }
    result[key] = value;
  }
  return result;
}

export function isObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function safeJsonParse(raw, fallback = null) {
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

export function createElement(tag, attributes = {}, text = "") {
  const element = document.createElement(tag);
  for (const [key, value] of Object.entries(attributes)) {
    if (value === null || value === undefined) {
      continue;
    }
    if (key === "className") {
      element.className = value;
      continue;
    }
    if (key === "dataset" && isObject(value)) {
      Object.entries(value).forEach(([dKey, dValue]) => {
        element.dataset[dKey] = String(dValue);
      });
      continue;
    }
    element.setAttribute(key, String(value));
  }
  if (text) {
    element.textContent = text;
  }
  return element;
}

export function isVisible(element) {
  if (!element) {
    return false;
  }
  const style = window.getComputedStyle(element);
  if (
    style.display === "none" ||
    style.visibility === "hidden" ||
    style.opacity === "0"
  ) {
    return false;
  }
  if (!element.getClientRects || element.getClientRects().length === 0) {
    return false;
  }
  return true;
}

export function focusNextBySelector(selector, state) {
  const nodes = Array.from(document.querySelectorAll(selector)).filter(
    (node) => !node.disabled && isVisible(node),
  );
  if (nodes.length < 1) {
    return;
  }
  const current = state.index ?? -1;
  const next = current >= nodes.length - 1 ? 0 : current + 1;
  state.index = next;
  nodes[next].focus();
  if (typeof nodes[next].scrollIntoView === "function") {
    nodes[next].scrollIntoView({ behavior: "smooth", block: "center" });
  }
}

export function parseHotkey(hotkey) {
  if (!hotkey || typeof hotkey !== "string") {
    return null;
  }
  const tokens = hotkey.toLowerCase().split("+").map((item) => item.trim());
  const key = tokens.pop();
  if (!key) {
    return null;
  }
  return {
    key,
    alt: tokens.includes("alt"),
    shift: tokens.includes("shift"),
    ctrl: tokens.includes("ctrl") || tokens.includes("control"),
    meta: tokens.includes("meta") || tokens.includes("cmd") || tokens.includes("command"),
  };
}

export function hotkeyMatches(event, parsedHotkey) {
  if (!parsedHotkey) {
    return false;
  }
  const pressedKey = event.key.toLowerCase();
  return (
    pressedKey === parsedHotkey.key &&
    Boolean(event.altKey) === parsedHotkey.alt &&
    Boolean(event.shiftKey) === parsedHotkey.shift &&
    Boolean(event.ctrlKey) === parsedHotkey.ctrl &&
    Boolean(event.metaKey) === parsedHotkey.meta
  );
}

export function withTryCatch(handler) {
  try {
    handler();
  } catch (error) {
    // Keep runtime resilient in third-party pages.
    console.warn("[ReadlyGo] runtime warning", error);
  }
}

export function hasSpeechSynthesis() {
  return (
    typeof window !== "undefined" &&
    "speechSynthesis" in window &&
    "SpeechSynthesisUtterance" in window
  );
}

export function getSpeechRecognitionCtor() {
  if (typeof window === "undefined") {
    return null;
  }
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}
